import React, { useState } from "react";
import { ScopeSelect } from "../ScopeSelect";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from "../ui/dialog";
import { Button } from "../ui/button";
import { toast } from "../ui/sonner";
import { Loader2, HardHat } from "lucide-react";
import { TEAMS } from "../../data/constants";

const todayLA = () => new Intl.DateTimeFormat("en-CA", { timeZone: "America/Los_Angeles", year: "numeric", month: "2-digit", day: "2-digit" }).format(new Date());
const NATIVE = "mt-1 flex h-9 w-full rounded-md border border-input bg-background px-3 py-1.5 text-sm focus:outline-none focus:ring-2 focus:ring-ring";
const BR_TEAM = { la: "team_1", norcal: "team_norcal", sd: "team_sd" };
const money = (n) => `$${Number(n || 0).toLocaleString("en-US", { maximumFractionDigits: 0 })}`;

export function QbCreateProjectDialog({ api, zh, previewId, row, phases, onClose, onCreated }) {
  const [phase, setPhase] = useState("0");
  const [team, setTeam] = useState(BR_TEAM[row?.branch] || "team_1");
  const [started, setStarted] = useState(todayLA());
  const [client, setClient] = useState("");
  const [phone, setPhone] = useState("");
  const [busy, setBusy] = useState(false);
  if (!row) return null;
  const list = (phases || []).map((n, i) => ({ idx: i, name: n }));

  const submit = async () => {
    const name = list.find((p) => p.idx === Number(phase))?.name;
    if (!window.confirm(zh ? `确认把「${row.address}」建为在建项目？\n阶段：${Number(phase) + 1}. ${name} · 施工组：${TEAMS.find((t) => t.id === team)?.zh || team} · 合同 ${money(row.contract)}\n收款计划按 QuickBooks 开票 / 收款写入，之前阶段任务标「历史项目导入回填」。` : `Create "${row.address}" as an in-progress project?`)) return;
    setBusy(true);
    try {
      const { data } = await api.post(`/projects/import/qb/preview/${previewId}/create-project`, { key: row.key, phaseIdx: Number(phase), team, startedAt: started, clientName: client.trim(), phone: phone.trim() });
      toast.success(zh ? `已创建项目 ${data.projectId}（阶段 ${data.phaseIdx + 1}）` : `Project ${data.projectId} created`);
      onCreated?.(row.key, data);
      onClose?.();
    } catch (e) { toast.error(e?.response?.data?.detail || (zh ? "创建失败" : "Failed")); }
    finally { setBusy(false); }
  };

  return (
    <Dialog open onOpenChange={(o) => !o && onClose?.()}>
      <DialogContent className="max-w-md" data-testid="qb-create-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2"><HardHat className="h-4 w-4 text-emerald-600" />{zh ? "转为在建项目" : "Create in-progress project"}</DialogTitle>
          <DialogDescription className="text-xs">{row.address} · {zh ? "合同" : "Contract"} {money(row.contract)} · {zh ? "已收" : "Received"} {money(row.received)} · A/R {money(row.ar)}</DialogDescription>
        </DialogHeader>
        <div className="space-y-3">
          <div>
            <label className="text-sm font-medium text-slate-700">{zh ? "目前施工阶段" : "Current phase"}</label>
            <select value={phase} onChange={(e) => setPhase(e.target.value)} className={NATIVE} data-testid="qb-create-phase">
              {list.map((p) => <option key={p.idx} value={p.idx}>{p.idx + 1}. {p.name}</option>)}
            </select>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="text-sm font-medium text-slate-700">{zh ? "施工组" : "Crew"}</label>
              <ScopeSelect kind="team" value={team} onChange={(e) => setTeam(e.target.value)} className={NATIVE} data-testid="qb-create-team">
                {TEAMS.map((t) => <option key={t.id} value={t.id}>{zh ? t.zh : t.name}</option>)}
              </ScopeSelect>
            </div>
            <div>
              <label className="text-sm font-medium text-slate-700">{zh ? "阶段起始日期" : "Phase start"}</label>
              <input type="date" value={started} max={todayLA()} onChange={(e) => setStarted(e.target.value)} className={NATIVE} data-testid="qb-create-started" />
            </div>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="text-sm font-medium text-slate-700">{zh ? "客户姓名（可留空）" : "Client (optional)"}</label>
              <input value={client} onChange={(e) => setClient(e.target.value)} className={NATIVE} placeholder={row.customers?.[0] !== row.address ? row.customers?.[0] : ""} data-testid="qb-create-client" />
            </div>
            <div>
              <label className="text-sm font-medium text-slate-700">{zh ? "客户电话（可留空）" : "Phone (optional)"}</label>
              <input value={phone} onChange={(e) => setPhone(e.target.value)} className={NATIVE} data-testid="qb-create-phone" />
            </div>
          </div>
          <div className="flex justify-end gap-2 pt-1">
            <Button variant="outline" size="sm" onClick={onClose} data-testid="qb-create-cancel">{zh ? "取消" : "Cancel"}</Button>
            <Button size="sm" onClick={submit} disabled={busy} className="bg-emerald-600 hover:bg-emerald-700 text-white" data-testid="qb-create-submit">{busy && <Loader2 className="h-3.5 w-3.5 animate-spin mr-1" />}{zh ? "创建在建项目" : "Create"}</Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}
